import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { authService, claimPendingReferral, REFERRAL_CODE_KEY } from '../services/api';
import { pickLoginMessage } from '../utils/loginMessage';
import { User, Lock, Mail, MailCheck, UserPlus, Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

interface AuthTabsProps {
  id?: string;
  activeTab?: 'login' | 'register';
  onTabChange?: (tab: 'login' | 'register') => void;
}

const AuthTabs: React.FC<AuthTabsProps> = ({ id, activeTab: controlledTab, onTabChange }) => {
  const [internalTab, setInternalTab] = useState<'login' | 'register'>('login');
  const [showForgot, setShowForgot] = useState(false);
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: 'login' | 'register') => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const navigate = useNavigate();

  return (
    <div id={id} className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-card shadow-elevated border border-zinc-200 overflow-hidden">
        {showForgot ? (
          <ForgotPasswordForm onBack={() => setShowForgot(false)} />
        ) : (
          <>
        <div className="flex border-b border-zinc-200">
          <button
            onClick={() => setActiveTab('login')}
            className={`flex-1 py-3.5 text-sm font-medium transition-colors relative ${
              activeTab === 'login' ? 'text-brand-500' : 'text-secondary-foreground hover:text-foreground'
            }`}
          >
            Sign In
            {activeTab === 'login' && (
              <motion.div
                layoutId="auth-tab-indicator"
                className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand-500 rounded-full"
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`flex-1 py-3.5 text-sm font-medium transition-colors relative ${
              activeTab === 'register' ? 'text-brand-500' : 'text-secondary-foreground hover:text-foreground'
            }`}
          >
            Create Account
            {activeTab === 'register' && (
              <motion.div
                layoutId="auth-tab-indicator"
                className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand-500 rounded-full"
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'login' ? (
            <LoginForm key="login" onSuccess={() => navigate('/dashboard')} onSwitchTab={() => setActiveTab('register')} onForgotPassword={() => setShowForgot(true)} />
          ) : (
            <RegisterForm key="register" onSuccess={() => navigate('/dashboard')} onSwitchTab={() => setActiveTab('login')} />
          )}
        </AnimatePresence>

        <div className="px-6 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-secondary-foreground font-medium">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>
          <button
            onClick={() => {
              const redirectUri = `${window.location.origin}/auth/callback`;
              const authUrl = `${window.location.origin}/realms/resume-realm/protocol/openid-connect/auth?client_id=resume-auth-service&response_type=code&scope=openid+profile+email&redirect_uri=${encodeURIComponent(redirectUri)}&kc_idp_hint=linkedin`;
              window.location.href = authUrl;
            }}
            className="w-full h-10 flex items-center justify-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white text-sm font-medium rounded-btn transition-all duration-200"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            Sign in with LinkedIn
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

interface LoginFormProps {
  onSuccess: () => void;
  onSwitchTab: () => void;
  onForgotPassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchTab, onForgotPassword }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setIsLoading(true);
    try {
      const response = await authService.login({ username, password });
      if (response.data.success) {
        await login(response.data.data);
        void claimPendingReferral();
        toast.success(pickLoginMessage(response.data.data?.firstLogin));
        onSuccess();
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      const msg = e.response?.data?.message || 'Invalid credentials. Please try again.';
      setError(msg);
      setNeedsVerification(msg.toLowerCase().includes('verify your email'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!username || isResending) return;
    setIsResending(true);
    try {
      await authService.resendVerification(username);
      toast.success('Verification email sent. Check your inbox.');
    } catch {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <motion.div
      key="login"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="p-6"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-username" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Username
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); setNeedsVerification(false); }}
              required
              placeholder="Enter your username"
              className={`w-full h-10 pl-10 pr-3 text-sm border rounded-btn transition-all duration-200 bg-white ${
                error ? 'border-red-400 focus:ring-red-200' : 'border-zinc-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
              } focus:outline-none`}
            />
          </div>
        </div>

        <div>
          <label htmlFor="login-password" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); setNeedsVerification(false); }}
              required
              placeholder="Enter your password"
              className={`w-full h-10 pl-10 pr-10 text-sm border rounded-btn transition-all duration-200 bg-white ${
                error ? 'border-red-400 focus:ring-red-200' : 'border-zinc-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
              } focus:outline-none`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-btn transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              className="bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2 rounded-btn overflow-hidden"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {needsVerification && (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="w-full h-9 flex items-center justify-center gap-2 text-xs font-medium text-brand-500 hover:text-brand-600 border border-brand-200 hover:bg-brand-50 rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? (
              <div className="w-3.5 h-3.5 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
            ) : (
              <>
                <MailCheck className="w-3.5 h-3.5" />
                Resend Verification Email
              </>
            )}
          </button>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-10 flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200"
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Sign In
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      <p className="mt-4 text-xs text-center text-secondary-foreground">
        Don't have an account?{' '}
        <button onClick={onSwitchTab} className="text-brand-500 hover:text-brand-600 font-medium transition-colors">
          Create one
        </button>
      </p>
      <p className="mt-2 text-xs text-center">
        <button
          onClick={onForgotPassword}
          className="text-zinc-500 hover:text-brand-600 font-medium transition-colors"
        >
          Forgot your password?
        </button>
      </p>
    </motion.div>
  );
};

interface ForgotPasswordFormProps {
  onBack: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await authService.forgotPassword({ email });
      setSent(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      key="forgot"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="p-6"
    >
      {sent ? (
        <div className="text-center py-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <MailCheck className="w-6 h-6 text-green-500" />
          </div>
          <h3 className="text-sm font-semibold text-zinc-800 mb-1.5">Check your email</h3>
          <p className="text-xs text-zinc-500 leading-relaxed mb-5">
            If an account exists for <span className="text-zinc-700 font-medium">{email}</span>, we've sent
            a link to reset your password.
          </p>
          <button
            onClick={onBack}
            className="w-full h-10 flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200"
          >
            Back to Sign In
          </button>
        </div>
      ) : (
        <>
          <div className="text-center mb-5">
            <div className="mx-auto w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center mb-3">
              <Lock className="w-6 h-6 text-brand-500" />
            </div>
            <h3 className="text-sm font-semibold text-zinc-800">Forgot your password?</h3>
            <p className="text-xs text-zinc-500 mt-1">
              Enter your email and we'll send you a link to reset it.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="forgot-email" className="block text-xs font-medium text-zinc-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required
                  placeholder="Enter your email"
                  className={`w-full h-10 pl-10 pr-3 text-sm border rounded-btn transition-all duration-200 bg-white ${
                    error ? 'border-red-400 focus:ring-red-200' : 'border-zinc-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
                  } focus:outline-none`}
                />
              </div>
            </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              className="bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2 rounded-btn overflow-hidden"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-10 flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200"
        >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Send Reset Link
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
          <button
            onClick={onBack}
            className="mt-4 w-full flex items-center justify-center gap-1.5 text-xs text-secondary-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Sign In
          </button>
        </>
      )}
    </motion.div>
  );
};

interface RegisterFormProps {
  onSuccess: () => void;
  onSwitchTab: () => void;
}

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchTab }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const { login } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setEmailError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!EMAIL_REGEX.test(formData.email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      if (response.data.success) {
        const refClaimed = response.data.data.referralClaimed;
        const refReason = response.data.data.referralReason;
        if (refClaimed) {
          toast.success('Referral code applied!');
        } else if (refReason) {
          toast.error(`Referral could not be applied: ${refReason}`);
        }
        localStorage.removeItem(REFERRAL_CODE_KEY);
        try {
          const loginRes = await authService.login({
            username: formData.username,
            password: formData.password,
          });
          if (loginRes.data.success) {
            await login(loginRes.data.data);
            onSuccess();
            return;
          }
        } catch {
          // login after register failed, redirect to login tab
        }
        onSwitchTab();
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      key="register"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="p-6"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="reg-firstName" className="block text-xs font-medium text-zinc-700 mb-1.5">
              First Name
            </label>
            <input
              id="reg-firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder="John"
              className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all duration-200 bg-white"
            />
          </div>
          <div>
            <label htmlFor="reg-lastName" className="block text-xs font-medium text-zinc-700 mb-1.5">
              Last Name
            </label>
            <input
              id="reg-lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              required
              placeholder="Doe"
              className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all duration-200 bg-white"
            />
          </div>
        </div>

        <div>
          <label htmlFor="reg-username" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Username
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="reg-username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="Choose a username"
              className="w-full h-10 pl-10 pr-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all duration-200 bg-white"
            />
          </div>
        </div>

        <div>
          <label htmlFor="reg-email" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="reg-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john@example.com"
              className={`w-full h-10 pl-10 pr-3 text-sm border rounded-btn transition-all duration-200 bg-white ${
                emailError ? 'border-red-400 focus:ring-red-200' : 'border-zinc-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
              } focus:outline-none`}
            />
          </div>
          {emailError && (
            <p className="mt-1.5 text-xs text-red-600">{emailError}</p>
          )}
        </div>

        <div>
          <label htmlFor="reg-password" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="reg-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              placeholder="Min 6 characters"
              className="w-full h-10 pl-10 pr-10 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all duration-200 bg-white"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-btn transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="reg-confirmPassword" className="block text-xs font-medium text-zinc-700 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              id="reg-confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Confirm your password"
              className="w-full h-10 pl-10 pr-3 text-sm border border-zinc-200 rounded-btn focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all duration-200 bg-white"
            />
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              className="bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2 rounded-btn overflow-hidden"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-10 flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200"
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Create Account
            </>
          )}
        </button>
      </form>

      <p className="mt-4 text-xs text-center text-secondary-foreground">
        Already have an account?{' '}
        <button onClick={onSwitchTab} className="text-brand-500 hover:text-brand-600 font-medium transition-colors">
          Sign in
        </button>
      </p>
    </motion.div>
  );
};

export default AuthTabs;
